import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
// ImageCropper — модальная квадратная обрезка изображения при выборе (аватары группы/профиля).
// Самодостаточен (без внешних либ, CSP-friendly): <img> с transform, drag пальцем/мышью (Pointer
// Events + setPointerCapture), зум слайдером; «Готово» рисует выбранную область на canvas
// outputSize×outputSize и отдаёт JPEG-blob. Побочный выигрыш: любое фото (включая HEIC, который
// Safari декодирует нативно) уходит на сервер маленьким JPEG — лимиты размера недостижимы.
// Геометрия (cover/кламп/зум вокруг центра) — чистые функции util/crop (юниты там, canvas — browser-verify).
import { useCallback, useEffect, useRef, useState } from 'react';
import { Modal } from '../primitives/Modal.js';
import { Button } from '../primitives/Button.js';
import { clampOffset, coverScale, cropSourceRect, zoomAroundCenter } from '../util/crop.js';
const VIEWPORT = 280;
const MAX_ZOOM_FACTOR = 4;
export function ImageCropper({ file, outputSize = 512, onCropped, onCancel, labels, }) {
    const [url, setUrl] = useState(null);
    const [geo, setGeo] = useState(null);
    const [busy, setBusy] = useState(false);
    const imgRef = useRef(null);
    const drag = useRef(null);
    // object-URL живёт, пока открыт диалог
    useEffect(() => {
        if (file === null) {
            setUrl(null);
            setGeo(null);
            return;
        }
        const u = URL.createObjectURL(file);
        setUrl(u);
        return () => URL.revokeObjectURL(u);
    }, [file]);
    const onImgLoad = useCallback(() => {
        const img = imgRef.current;
        if (img === null)
            return;
        const s = coverScale(img.naturalWidth, img.naturalHeight, VIEWPORT);
        // стартуем по центру
        const ox = (VIEWPORT - img.naturalWidth * s) / 2;
        const oy = (VIEWPORT - img.naturalHeight * s) / 2;
        setGeo({ naturalW: img.naturalWidth, naturalH: img.naturalHeight, scale: s, ox, oy });
    }, []);
    const onPointerDown = useCallback((e) => {
        if (geo === null)
            return;
        e.preventDefault();
        e.target.setPointerCapture?.(e.pointerId);
        drag.current = { x: e.clientX, y: e.clientY, ox: geo.ox, oy: geo.oy };
    }, [geo]);
    const onPointerMove = useCallback((e) => {
        const d = drag.current;
        if (d === null || geo === null)
            return;
        const next = clampOffset(d.ox + (e.clientX - d.x), d.oy + (e.clientY - d.y), geo.naturalW, geo.naturalH, geo.scale, VIEWPORT);
        setGeo({ ...geo, ...next });
    }, [geo]);
    const onPointerUp = useCallback(() => {
        drag.current = null;
    }, []);
    const onZoom = useCallback((value) => {
        if (geo === null)
            return;
        const min = coverScale(geo.naturalW, geo.naturalH, VIEWPORT);
        const nextScale = Math.max(min, Math.min(min * MAX_ZOOM_FACTOR, value));
        const next = zoomAroundCenter(geo.ox, geo.oy, geo.scale, nextScale, geo.naturalW, geo.naturalH, VIEWPORT);
        setGeo({ ...geo, scale: nextScale, ...next });
    }, [geo]);
    const done = useCallback(() => {
        const img = imgRef.current;
        if (img === null || geo === null)
            return;
        setBusy(true);
        const { sx, sy, size } = cropSourceRect(geo.ox, geo.oy, geo.scale, VIEWPORT);
        const canvas = document.createElement('canvas');
        canvas.width = outputSize;
        canvas.height = outputSize;
        const ctx = canvas.getContext('2d');
        if (ctx === null) {
            setBusy(false);
            return;
        }
        ctx.drawImage(img, sx, sy, size, size, 0, 0, outputSize, outputSize);
        canvas.toBlob((blob) => {
            setBusy(false);
            if (blob !== null)
                onCropped(blob);
        }, 'image/jpeg', 0.86);
    }, [geo, outputSize, onCropped]);
    if (file === null)
        return null;
    const minScale = geo === null ? 1 : coverScale(geo.naturalW, geo.naturalH, VIEWPORT);
    const frame = {
        position: 'relative',
        width: `${VIEWPORT}px`,
        height: `${VIEWPORT}px`,
        overflow: 'hidden',
        borderRadius: 'var(--radius-card)',
        background: 'var(--color-bg-secondary)',
        touchAction: 'none', // drag пальцем — не скролл
        cursor: 'grab',
        alignSelf: 'center',
    };
    return (_jsx(Modal, { open: true, onClose: onCancel, label: labels?.title ?? 'Обрезка фото', children: _jsxs("div", { "data-image-cropper": "", style: { display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }, children: [_jsx("h3", { style: { margin: 0 }, children: labels?.title ?? 'Обрезка фото' }), _jsxs("div", { "data-crop-frame": "", style: frame, onPointerDown: onPointerDown, onPointerMove: onPointerMove, onPointerUp: onPointerUp, onPointerCancel: onPointerUp, children: [url !== null ? (_jsx("img", { ref: imgRef, src: url, alt: "", draggable: false, onLoad: onImgLoad, style: {
                                position: 'absolute',
                                left: 0,
                                top: 0,
                                transformOrigin: '0 0',
                                transform: geo === null ? undefined : `translate(${geo.ox}px, ${geo.oy}px) scale(${geo.scale})`,
                                userSelect: 'none',
                                pointerEvents: 'none',
                                maxWidth: 'none', // глобальный reset img не должен ужимать оригинал
                            } })) : null, _jsx("div", { style: {
                                position: 'absolute',
                                inset: 0,
                                borderRadius: '50%',
                                boxShadow: '0 0 0 999px rgba(0,0,0,0.35)',
                                pointerEvents: 'none',
                            } })] }), _jsx("input", { "data-crop-zoom": "", type: "range", "aria-label": labels?.zoom ?? 'Масштаб', min: minScale, max: minScale * MAX_ZOOM_FACTOR, step: minScale / 50, value: geo?.scale ?? minScale, onChange: (e) => onZoom(Number(e.target.value)), disabled: geo === null }), _jsxs("div", { style: { display: 'flex', gap: 'var(--space-2)', justifyContent: 'flex-end' }, children: [_jsx(Button, { variant: "ghost", onClick: onCancel, disabled: busy, children: labels?.cancel ?? 'Отмена' }), _jsx(Button, { variant: "primary", "data-crop-done": "", onClick: done, disabled: busy || geo === null, children: labels?.done ?? 'Готово' })] })] }) }));
}
//# sourceMappingURL=ImageCropper.js.map